/**
 * An app's tile, drawn the one way every surface draws it (RFC 0332 3d).
 *
 * The tile is `appIcon` from core, a pure function of the app's catalog entry,
 * shown as an `<img>` so the ids inside two tiles on one page never collide and
 * nothing in the SVG can run. The catalog is handed down once by the shell
 * (`AppIconsProvider`), so a row that knows only an app id, or only the data
 * type of a record, can still draw its app without reading anything.
 *
 * A service rather than a component in one feature, because the shell and
 * `features/packages` both draw it and a feature may not import another.
 */

import {
  type AppSummary,
  appIconUrl,
  distinctCodes,
  type IconApp,
  type IconSpec,
} from '@mod/core/model'
import { createContext, type ReactNode, useContext, useMemo } from 'react'

interface IconEntry {
  id: string
  name: string
  /** With the name and description, what `generateIcon` reads for an app
   *  that has no stored icon yet, so it draws what would be stored. */
  dataType?: string
  description?: string
  icon?: IconSpec
}

interface AppIcons {
  byId: ReadonlyMap<string, IconEntry>
  byDataType: ReadonlyMap<string, IconEntry>
  /** The app that opens a file of this mime, when one claims it: the Text
   *  editor for markdown. Handed in by the shell, which knows the person's
   *  editor choices. */
  editorFor: (mime: string) => string | undefined
  /** Codes no two alike across the catalog; only tiles of 32 px and up show one. */
  codes: ReadonlyMap<string, string>
}

const EMPTY: AppIcons = {
  byId: new Map(),
  byDataType: new Map(),
  editorFor: () => undefined,
  codes: new Map(),
}
const AppIconsContext = createContext<AppIcons>(EMPTY)

/** Hand the installed apps to every tile below. The shell mounts this once. */
export function AppIconsProvider({
  apps,
  editorFor,
  children,
}: {
  apps: readonly AppSummary[]
  /** Which app opens a file of this mime (the shell's `handlerFor`). */
  editorFor?: (mime: string) => string | undefined
  children: ReactNode
}) {
  const value = useMemo<AppIcons>(() => {
    const byId = new Map<string, IconEntry>()
    const byDataType = new Map<string, IconEntry>()
    for (const a of apps) {
      const entry: IconEntry = {
        id: a.id,
        name: a.name,
        ...(a.dataType ? { dataType: a.dataType } : {}),
        ...(a.description ? { description: a.description } : {}),
        ...(a.icon ? { icon: a.icon } : {}),
      }
      byId.set(a.id, entry)
      // The view document opens an app by name, so a row that holds only the
      // view's id still finds its app.
      if (a.viewDocId) byId.set(a.viewDocId, entry)
      if (a.dataType && !byDataType.has(a.dataType))
        byDataType.set(a.dataType, entry)
    }
    return {
      byId,
      byDataType,
      editorFor: editorFor ?? EMPTY.editorFor,
      codes: distinctCodes(
        apps.map((a) => ({ id: a.id, name: a.name, icon: a.icon })),
      ),
    }
  }, [apps, editorFor])
  return (
    <AppIconsContext.Provider value={value}>
      {children}
    </AppIconsContext.Provider>
  )
}

/** The app a row belongs to, by its id (or view id) or by a record's data type. */
export function useIconApp(ref: {
  appId?: string
  dataType?: string
  /** A plain file's mime: its tile is the app that opens it. */
  mime?: string
}): IconEntry | undefined {
  const icons = useContext(AppIconsContext)
  const editor = ref.mime ? icons.editorFor(ref.mime) : undefined
  return (
    (ref.appId ? icons.byId.get(ref.appId) : undefined) ??
    (ref.dataType ? icons.byDataType.get(ref.dataType) : undefined) ??
    (editor ? icons.byId.get(editor) : undefined)
  )
}

/**
 * The tile, or `fallback` when the row's app is not installed (a record whose
 * app is gone, a document of no app). `size` is the size it is shown at, which
 * also decides what it draws: the full tile from 48 px, letter and drawing
 * from 24, the drawing alone on its colour below that.
 */
export function AppIcon({
  appId,
  dataType,
  mime,
  size,
  className,
  fallback = null,
}: {
  appId?: string
  dataType?: string
  /** A plain file's mime, so it draws the app that opens it. */
  mime?: string
  size: number
  className?: string
  fallback?: ReactNode
}) {
  const icons = useContext(AppIconsContext)
  const app = useIconApp({ appId, dataType, mime })
  if (!app) return <>{fallback}</>
  return (
    <IconTile
      spec={app.icon}
      app={app}
      size={size}
      code={icons.codes.get(app.id)}
      className={className}
    />
  )
}

/**
 * A tile for an app that need not be installed: a registry entry's icon, drawn
 * the same way as an installed app's (RFC 0279). `app` is what fills in the
 * fields the spec leaves out.
 */
export function IconTile({
  spec,
  app,
  size,
  code,
  className,
}: {
  spec: IconSpec | undefined
  app: IconApp
  size: number
  code?: string
  className?: string
}) {
  const src = useMemo(
    () => appIconUrl(spec, app, size, code),
    [spec, app, size, code],
  )
  return (
    <img
      src={src}
      alt=""
      aria-hidden
      width={size}
      height={size}
      draggable={false}
      className={`shrink-0 ${className ?? ''}`}
      style={{
        width: size,
        height: size,
        borderRadius: Math.max(2, Math.round(size * 0.14)),
      }}
    />
  )
}
